import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpDown } from "lucide-react";
import useUser from "../../../../hooks/useUser";
import Button from "../../../components/Buttons/Button/Button";
import Pagination from "../../../components/Pagination/Pagination";
import { getCreditPurchases } from "../../../../services/subscriptionsService";
import "./CreditUsage.css";

function CreditUsage() {
  const { user } = useUser();
  const { t } = useTranslation("suscriptions");
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);

  const [page, setPage] = useState(1);
  const [sortColumn, setSortColumn] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");

  const PAGE_SIZE = 10;

  const organizationId = useMemo(() => {
    const org = user?.organization || user?.organization_id;
    if (!org) return null;
    if (typeof org === "object") {
      return org.id || org._id || null;
    }
    return org;
  }, [user?.organization, user?.organization_id]);

  useEffect(() => {
    if (!organizationId) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getCreditPurchases();
        const list = Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data)
          ? data
          : [];
        setRows(list);
      } catch (e) {
        console.error("Error loading credit purchases", e?.response || e);
        setError(t("page.creditPurchases.error"));
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [organizationId, t]);

  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    // Same style as PaymentHistory: rely on browser/locale default
    return d.toLocaleDateString();
  };

  const getCredits = (item) => {
    return (
      item?.credits_used ??
      item?.credits ??
      item?.amount ??
      item?.quantity ??
      "-"
    );
  };

  const getPayment = (item) => {
    if (typeof item?.amount_paid === "number") {
      const amount = item.amount_paid / 100;
      return `USD ${amount.toFixed(2)}`;
    }
    if (typeof item?.amount_display === "string") {
      return item.amount_display;
    }
    if (typeof item?.total === "number") {
      const amount = item.total / 100;
      return `USD ${amount.toFixed(2)}`;
    }
    return "-";
  };

  const getSortValue = useCallback((item, column) => {
    if (!item) return null;

    if (column === "date") {
      const dateRaw =
        item?.payment_date ||
        item?.created ||
        item?.created_at ||
        item?.date ||
        item?.timestamp ||
        null;
      const d = dateRaw ? new Date(dateRaw) : null;
      return d && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
    }

    if (column === "credits") {
      const credits = getCredits(item);
      const n = Number(credits);
      return Number.isFinite(n) ? n : 0;
    }

    if (column === "payment") {
      if (typeof item?.amount_paid === "number") return item.amount_paid;
      if (typeof item?.total === "number") return item.total;
      return 0;
    }
    return 0;
  }, []);

  const sortedRows = useMemo(() => {
    if (!Array.isArray(rows) || rows.length === 0) return [];

    const sorted = [...rows].sort((a, b) => {
      const av = getSortValue(a, sortColumn);
      const bv = getSortValue(b, sortColumn);

      if (av === bv) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;

      if (sortDirection === "asc") {
        return av > bv ? 1 : -1;
      }
      return av < bv ? 1 : -1;
    });
    return sorted;
  }, [rows, sortColumn, sortDirection, getSortValue]);

  const totalPages = useMemo(() => {
    if (!sortedRows.length) return 1;
    return Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));
  }, [sortedRows.length]);

  const pagedRows = useMemo(() => {
    if (!sortedRows.length) return [];
    const currentPage = Math.min(page, totalPages);
    const start = (currentPage - 1) * PAGE_SIZE;
    return sortedRows.slice(start, start + PAGE_SIZE);
  }, [sortedRows, page, totalPages]);

  useEffect(() => {
    setPage(1);
  }, [sortColumn, sortDirection, rows.length]);

  const handleSort = (column) => {
    setSortColumn((prev) => {
      if (prev === column) {
        setSortDirection((dir) => (dir === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDirection("desc");
      return column;
    });
  };

  const renderSortableHeader = (columnKey, label) => (
    <button
      type="button"
      className="credit-usage-sortable"
      onClick={() => handleSort(columnKey)}
    >
      <span>{label}</span>
      <ArrowUpDown className="credit-usage-sort-icon" size={14} />
    </button>
  );

  return (
    <div className="credit-usage-page">
      <div className="credit-usage-header">
        <div>
          <h1 className="credit-usage-title">{t("page.creditPurchases.title")}</h1>
          <p className="credit-usage-subtitle">
            {t("page.creditPurchases.subtitle", {
              defaultValue: "See detailed history of your credit purchases.",
            })}
          </p>
        </div>
        <Button
          type="button"
          variant="primary"
          size="md"
          onClick={() => navigate("/app/suscriptions")}
        >
          {t("page.paymentHistory.backToSubscription")}
        </Button>
      </div>

      <div className="credit-usage-card">
        {loading && (
          <div className="credit-usage-message">
            {t("page.creditPurchases.loading")}
          </div>
        )}

        {error && !loading && (
          <div className="credit-usage-message credit-usage-message-error">
            {error}
          </div>
        )}

        {!loading && !error && rows.length === 0 && (
          <div className="credit-usage-message">
            {t("page.creditPurchases.empty")}
          </div>
        )}

        {!loading && !error && sortedRows.length > 0 && (
          <div className="credit-usage-table-wrapper">
            <table className="credit-usage-table">
              <thead>
                <tr>
                  <th>
                    {renderSortableHeader(
                      "date",
                      t("page.creditPurchases.columns.date")
                    )}
                  </th>
                  <th>
                    {renderSortableHeader(
                      "credits",
                      t("page.creditPurchases.columns.credits")
                    )}
                  </th>
                  <th>
                    {renderSortableHeader(
                      "payment",
                      t("page.creditPurchases.columns.payment")
                    )}
                  </th>
                  <th>{t("page.paymentHistory.table.invoice")}</th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map((item, index) => {
                  const key = item?.id ?? index;
                  const dateRaw =
                    item?.payment_date ||
                    item?.created ||
                    item?.created_at ||
                    item?.date ||
                    item?.timestamp ||
                    null;

                  const invoiceUrl =
                    item?.link_payment ||
                    item?.invoice_url ||
                    item?.hosted_invoice_url ||
                    item?.stripe_invoice_url ||
                    null;

                  return (
                    <tr key={key}>
                      <td>{formatDate(dateRaw)}</td>
                      <td>{getCredits(item)}</td>
                      <td>{getPayment(item)}</td>
                      <td>
                        {invoiceUrl ? (
                          <a
                            href={invoiceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="payment-history-link"
                            title={t("page.paymentHistory.table.downloadDetails")}
                          >
                            <svg
                              aria-hidden="true"
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              xmlns="http://www.w3.org/2000/svg"
                              className="⚙   as-y as-z as-10 as-11 as-4k as-7a as-7b as-8c as-3w as-3y ⚙1rbqim6"
                            >
                              <path d="M0 2.25A2.25 2.25 0 0 1 2.25 0h7.5A2.25 2.25 0 0 1 12 2.25v6a.75.75 0 0 1-1.5 0v-6a.75.75 0 0 0-.75-.75h-7.5a.75.75 0 0 0-.75.75v10.851a.192.192 0 0 0 .277.172l.888-.444a.75.75 0 1 1 .67 1.342l-.887.443A1.69 1.69 0 0 1 0 13.101V2.25Z"></path>
                              <path d="M5 10.7a.7.7 0 0 1 .7-.7h4.6a.7.7 0 1 1 0 1.4H7.36l.136.237c.098.17.193.336.284.491.283.483.554.907.855 1.263.572.675 1.249 1.109 2.365 1.109 1.18 0 2.038-.423 2.604-1.039.576-.626.896-1.5.896-2.461 0-.99-.42-1.567-.807-1.998a.75.75 0 1 1 1.115-1.004C15.319 8.568 16 9.49 16 11c0 1.288-.43 2.54-1.292 3.476C13.838 15.423 12.57 16 11 16c-1.634 0-2.706-.691-3.51-1.64-.386-.457-.71-.971-1.004-1.472L6.4 12.74v2.56a.7.7 0 1 1-1.4 0v-4.6ZM2.95 4.25a.75.75 0 0 1 .75-.75h2a.75.75 0 0 1 0 1.5h-2a.75.75 0 0 1-.75-.75ZM3.7 6.5a.75.75 0 0 0 0 1.5h4.6a.75.75 0 0 0 0-1.5H3.7Z"></path>
                            </svg>
                          </a>
                        ) : (
                          <span className="payment-history-link-disabled">
                            {t("page.paymentHistory.table.noInvoice")}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && !error && sortedRows.length > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          size="sm"
          className="credit-usage-pagination"
          infoBelow={
            <span className="credit-usage-page-info">
              {t("page.creditPurchases.pagination.pageInfo", {
                page,
                totalPages,
              })}
            </span>
          }
        />
      )}
    </div>
  );
}

export default CreditUsage;
